import type { RootState } from 'src/redux/store';
import type { Breakpoint } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';

import { useEffect } from 'react';
import { merge } from 'es-toolkit';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

import { _contacts } from 'src/utils/constants';

import { useTranslate } from 'src/locales';
import { usePremissionContext } from 'src/pages/auth/hooks';
import { useAppSelector, useAppDispatch } from 'src/hook/hooks';
import { useVoiceCallAllowed } from 'src/hook/use-voice-call-allowed';
import { setChannelFeatureFlags } from 'src/pages/auth/slice/authSlice';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';
import { setAISettings } from 'src/pages/setting/components/ai-settings/slice/aiSettingsSlice';
import { useGetAISettingsQuery } from 'src/pages/setting/components/ai-settings/slice/aiSettingsApi';
import {
  useGetRoleChannelAccessQuery,
  useGetChannelFeatureFlagsQuery,
} from 'src/pages/setting/components/channel-feature-flags/slice/channelFeatureFlagApi';

import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';

import { NavMobile } from './nav-mobile';
import { VerticalDivider } from './content';
import { NavVertical } from './nav-vertical';
import { DialerPopover } from './call-dialer';
import { InternalCall } from './internal-call';
import { layoutClasses } from '../core/classes';
import { NavHorizontal } from './nav-horizontal';
import { _account } from '../nav-config-account';
import { MainSection } from '../core/main-section';
import { MenuButton } from '../components/menu-button';
import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { AccountDrawer } from '../components/account-drawer';
import { BalanceDisplay } from '../components/balance-display';
import { LanguagePopover } from '../components/language-popover';
import { ToggleThemeSwitch } from '../components/dark-light-button';
import { WorkspacesPopover } from '../components/workspaces-popover';
import { navData as dashboardNavData } from '../nav-config-dashboard';
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars';
import { NotificationsDrawer } from '../components/notifications-drawer';
import { ChatAssignedTo } from '../components/invite-user/chat-assigned-to';

import type { MainSectionProps } from '../core/main-section';
import type { HeaderSectionProps } from '../core/header-section';
import type { LayoutSectionProps } from '../core/layout-section';

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: {
      data?: NavSectionProps['data'];
    };
    main?: MainSectionProps;
  };
};

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'lg',
}: DashboardLayoutProps) {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const settings = useSettingsContext();
  const { loginApiData } = useAppSelector((state: RootState) => state.auth);
  const { roleId } = useAppSelector((state: RootState) => state.auth);
  const { activeSubscription } = useAppSelector((state: RootState) => state.setting);
  const { t } = useTranslate('translation');
  const voiceCallAllowed = useVoiceCallAllowed();
  const { isView: isChannelView } = useModulePermissions('channel');
  /** Desktop dialer / internal call: show when VOICE channel flag allows outbound voice OR legacy Voice module view is granted. */
  const isVoiceFeatureVisible = isChannelView && voiceCallAllowed;
  const { data: aiSettingsData } = useGetAISettingsQuery();

  const { data: channelFlagsData } = useGetChannelFeatureFlagsQuery(undefined, {
    skip: !loginApiData,
    refetchOnMountOrArgChange: true,
  });

  const isSuperAdmin = Boolean(
    loginApiData && 'role' in loginApiData && loginApiData.role === 'Super Admin'
  );

  const { data: roleAccessData } = useGetRoleChannelAccessQuery(
    { roleId: roleId as string },
    { skip: !roleId || isSuperAdmin, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (aiSettingsData) {
      dispatch(setAISettings(aiSettingsData));
    }
  }, [aiSettingsData, dispatch]);

  useEffect(() => {
    if (!loginApiData) {
      dispatch(setChannelFeatureFlags({}));
      return;
    }

    if (channelFlagsData && (isSuperAdmin || roleAccessData || !roleId)) {
      const flagMap: Record<string, boolean> = {};

      // 1. Process Tenant-wide flags
      (channelFlagsData || []).forEach((item) => {
        const family = String(item.channel || '').toUpperCase();
        const capabilityKey = String(item.capabilityKey || '').toUpperCase();

        let isAllowed = item.platformBlocked === false;

        if (isSuperAdmin) {
          // Super Admins follow the tenant-wide flag
          isAllowed = isAllowed && item.enabled === true;
        } else {
          // Find the specific role permission for this capability
          const roleEntry = (roleAccessData || []).find(
            (r) => String(r.channel || '').toUpperCase() === capabilityKey
          );

          if (roleEntry) {
            // Use the role-specific permission
            isAllowed = isAllowed && roleEntry.isEnabled === true;
          } else {
            // Fall back to the tenant-wide flag if no role permission is defined
            isAllowed = isAllowed && item.enabled === true;
          }
        }

        if (family) {
          flagMap[family] = (flagMap[family] ?? false) || isAllowed;
        }
        if (capabilityKey) {
          flagMap[capabilityKey] = isAllowed;
        }
      });

      // 2. Ensure any channels explicitly denied in Role Access but missing from Tenant Flags are also recorded as false
      if (!isSuperAdmin && roleAccessData) {
        roleAccessData.forEach((r) => {
          const key = String(r.channel || '').toUpperCase();
          if (r.isEnabled === false && flagMap[key] === undefined) {
            flagMap[key] = false;
            // Also mark family as false if it's a family key
            const family = key.includes(':') ? key.split(':')[0]! : key;
            if (flagMap[family] === undefined) {
              flagMap[family] = false;
            }
          }
        });
      }

      dispatch(setChannelFeatureFlags(flagMap));
    }
  }, [channelFlagsData, roleAccessData, isSuperAdmin, roleId, dispatch, loginApiData]);
  const { handleNavPremissionCheck, hasPermission } = usePremissionContext();

  const navVars = dashboardNavColorVars(theme, settings.state.navColor, settings.state.navLayout);
  const { isView } = useModulePermissions('user');
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const navData = slotProps?.nav?.data ?? handleNavPremissionCheck(dashboardNavData);

  const isNavMini = settings.state.navLayout === 'mini';
  const isNavHorizontal = settings.state.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.state.navLayout === 'vertical';

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = {
      container: {
        maxWidth: false,
        sx: {
          ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
          ...(isNavHorizontal && {
            bgcolor: 'var(--layout-nav-bg)',
            height: { [layoutQuery]: 'var(--layout-nav-horizontal-height)' },
            [`& .${iconButtonClasses.root}`]: { color: 'var(--layout-nav-text-secondary-color)' },
          }),
        },
      },
    };

    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      bottomArea: isNavHorizontal ? (
        <NavHorizontal data={navData} layoutQuery={layoutQuery} cssVars={navVars.section} />
      ) : null,
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton
            onClick={onOpen}
            sx={{ mr: 1, ml: -1, [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }}
          />
          <NavMobile data={navData} open={open} onClose={onClose} cssVars={navVars.section} />

          {/** @slot Logo */}
          {isNavHorizontal && (
            <Logo
              sx={{
                display: 'none',
                [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
              }}
            />
          )}

          {/** @slot Divider */}
          {isNavHorizontal && (
            <VerticalDivider sx={{ [theme.breakpoints.up(layoutQuery)]: { display: 'flex' } }} />
          )}

          {/** @slot Workspace popover */}
          <WorkspacesPopover
            data={[loginApiData]}
            sx={{ color: 'var(--layout-nav-text-primary-color)' }}
          />
        </>
      ),
      rightArea: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          {/** @slot Balance display */}
          {loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' && (
              <BalanceDisplay
                variant="compact"
                noActivePlan={!activeSubscription}
                sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
              />
            )}

          {/** @slot Tools Area (Visible only on desktop) */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/** @slot Theme switch */}
            <ToggleThemeSwitch />

            {/** @slot Dialer button */}
            {loginApiData &&
              'tenant' in loginApiData &&
              typeof loginApiData.tenant === 'object' &&
              loginApiData.tenant !== null &&
              'name' in loginApiData.tenant &&
              (loginApiData.tenant as { name?: string }).name &&
              (loginApiData.tenant as { name?: string }).name !== 'MASTER' &&
              isVoiceFeatureVisible && <DialerPopover />}

            {/** @slot Internal call button */}
            {loginApiData &&
              'tenant' in loginApiData &&
              typeof loginApiData.tenant === 'object' &&
              loginApiData.tenant !== null &&
              'name' in loginApiData.tenant &&
              (loginApiData.tenant as { name?: string }).name &&
              (loginApiData.tenant as { name?: string }).name !== 'MASTER' &&
              isVoiceFeatureVisible && <InternalCall />}

            {/** @slot Assigned to button */}
            {isView &&
              loginApiData &&
              'tenant' in loginApiData &&
              typeof loginApiData.tenant === 'object' &&
              loginApiData.tenant !== null &&
              'name' in loginApiData.tenant &&
              (loginApiData.tenant as { name?: string }).name &&
              (loginApiData.tenant as { name?: string }).name !== 'MASTER' && (
                <ChatAssignedTo data={_contacts} />
              )}
          </Box>

          {/** @slot Language popover */}
          <LanguagePopover
            sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
            data={[
              { value: 'en', label: 'English', countryCode: 'GB' },
              { value: 'ar', label: 'Arabic', countryCode: 'SA' },
            ]}
          />

          {/** @slot Notifications popover */}
          {loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' &&
            (isSuperAdmin || hasPermission('notification')) && <NotificationsDrawer />}

          {/** @slot Account drawer */}
          <AccountDrawer data={_account(t)} />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      onToggleNav={() =>
        settings.setField(
          'navLayout',
          settings.state.navLayout === 'vertical' ? 'mini' : 'vertical'
        )
      }
    />
  );

  const renderFooter = () => null;

  const renderMain = () => <MainSection {...slotProps?.main}>{children}</MainSection>;

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Sidebar
       *************************************** */
      sidebarSection={isNavHorizontal ? null : renderSidebar()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
